/**
 * Shared destination for the staged US→driveshare.com redirect (task #677).
 *
 * The default lives in code so the app keeps working with no DB read; ops can
 * override it at runtime by setting an active `us_redirect_url` row in
 * `platform_config`, which the API exposes via `/api/config/platform`.
 */
export const DRIVESHARE_REDIRECT_URL = "https://driveshare.com";

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

interface PlatformConfigSnapshot {
  url: string;
  crossBrandEnabled: boolean;
}

let cached: PlatformConfigSnapshot | null = null;
let inflight: Promise<PlatformConfigSnapshot> | null = null;

function fetchSnapshot(): Promise<PlatformConfigSnapshot> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = fetch(`${API_BASE}/config/platform`)
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      const url = (data && typeof data.usRedirectUrl === "string" && data.usRedirectUrl.trim())
        ? data.usRedirectUrl.trim()
        : DRIVESHARE_REDIRECT_URL;
      // Default OFF so we never bounce visitors out of the preview — only
      // an explicit `true` from the API turns the cross-brand 301 on.
      const crossBrandEnabled = !!(data && data.usCrossBrandRedirectEnabled === true);
      cached = { url, crossBrandEnabled };
      return cached;
    })
    .catch(() => ({ url: DRIVESHARE_REDIRECT_URL, crossBrandEnabled: false }))
    .finally(() => { inflight = null; });
  return inflight;
}

export async function getDriveShareRedirectUrl(): Promise<string> {
  const snap = await fetchSnapshot();
  return snap.url;
}

/**
 * Resolve the `feature_us_cross_brand_redirect` flag (migration 049) on the
 * client. LocaleProvider gates its /us/* → driveshare.com replaceState on
 * this so the SPA doesn't kick visitors out of the dev preview while the
 * flag is OFF in dev/staging.
 */
export async function getUsCrossBrandRedirectEnabled(): Promise<boolean> {
  const snap = await fetchSnapshot();
  return snap.crossBrandEnabled;
}

export function buildDriveShareDeepLink(
  baseUrl: string,
  params: Record<string, string | number | null | undefined>,
): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === "") continue;
    sp.set(k, String(v));
  }
  const qs = sp.toString();
  if (!qs) return baseUrl;
  return `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}${qs}`;
}

/**
 * Build a deep link to a specific car on the DRIVESHARE destination.
 *
 * BookAClassic car URLs are `/cars/{id}/{slug}`; the matching DRIVESHARE URL
 * is `/car/{slug}-{id}` — e.g.
 *   bookaclassic.com/cars/1825/1930-austin-american
 *   → driveshare.com/car/1930-austin-american-1825
 *
 * When the car's identity is unknown (e.g. the blocked-US-search empty state
 * renders the handoff card with no car), we fall back to the base URL so the
 * visitor still lands on driveshare.com rather than a broken `/car/-` path.
 */
export function buildDriveShareCarUrl(
  baseUrl: string,
  car: { carId?: number | string | null; carSlug?: string | null },
  params: Record<string, string | number | null | undefined> = {},
): string {
  const slug = typeof car.carSlug === "string" ? car.carSlug.trim() : "";
  const id = car.carId == null ? "" : String(car.carId).trim();
  if (!slug || !id) return buildDriveShareDeepLink(baseUrl, params);
  const carPath = `${baseUrl.replace(/\/+$/, "")}/car/${slug}-${id}`;
  return buildDriveShareDeepLink(carPath, params);
}

/**
 * Build a deep link to DRIVESHARE's list-a-car flow (task #4401).
 *
 * The destination domain is resolved by the existing `getDriveShareRedirectUrl`
 * resolver (platform_config override → DRIVESHARE_REDIRECT_URL fallback); we
 * only append the `/list-a-car/` path and the supplied UTM params. The trailing
 * slash matches DRIVESHARE's canonical URL so we don't eat a 301.
 */
export function buildDriveShareListACarUrl(
  baseUrl: string,
  params: Record<string, string | number | null | undefined> = {},
): string {
  const listPath = `${baseUrl.replace(/\/+$/, "")}/list-a-car/`;
  return buildDriveShareDeepLink(listPath, params);
}

const OUTBOUND_CONTEXT_PREFIX = "us_redirect_outbound:";

/**
 * Log an outbound click to the DRIVESHARE destination through the existing
 * feature_flag_events pipeline so the same dashboard tracks adoption.
 */
export function logDriveShareOutbound(
  flagKey:
    | "feature_us_redirect_car_detail"
    | "feature_us_hide_from_search"
    | "feature_us_block_listing"
    | "feature_driveshare_login_redirect",
  userId: string | null,
  context: string,
): void {
  // Fire and forget; never block navigation on logging.
  try {
    fetch(`${API_BASE}/config/feature-flags/log`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        flag_key: flagKey,
        user_id: userId,
        variant: true,
        context: `${OUTBOUND_CONTEXT_PREFIX}${context}`,
      }),
    }).catch(() => { /* swallow */ });
  } catch { /* swallow */ }
}
